'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'my' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translation keys
const translations = {
  my: {
    // Header
    'home': 'ပင်မ',
    'games': 'ဂိမ်းများ',
    'profile': 'ကိုယ်ရေးကိုယ်တာ',
    'wallet': 'ပိုက်ဆံအိတ်',
    'cashin': 'ငွေထည့်',
    'cashout': 'ငွေထုတ်',
    'login': 'ဝင်ရောက်ရန်',
    'register': 'စာရင်းသွင်းရန်',
    'logout': 'ထွက်ရန်',
    'balance': 'လက်ကျန်ငွေ',
    'language': 'ဘာသာစကား',
    'myanmar': 'မြန်မာ',
    'english': 'အင်္ဂလိပ်',
    'welcome': 'မင်္ဂလာပါ',
    'buffalo_games': 'ကျွဲ စလော့ဂိမ်းများ',
    'promotions': 'ပရိုမိုးရှင်းများ',
    'invite': 'ဖိတ်ပေးခြင်း',
    'rewards': 'ဆုလာဘ်',
    'account': 'အကောင့်',
    'slot_games': 'စလော့ဂိမ်း',
    'fish_shooting': 'ငါးပစ်',
    'live_casino': 'တိုက်ရှိ',
    'lottery': 'လော်တရီ',
    'sports': 'အားကစား',
    'hot_games': 'ရေပန်းစား',
    'new_games': 'ဂိမ်းအသစ်',
    // Common
    'loading': 'ဖွင့်နေသည်...',
    'error': 'အမှားတစ်ခုဖြစ်ပွားခဲ့သည်',
    'success': 'အောင်မြင်ပါသည်',
    'save': 'သိမ်းဆည်းရန်',
    'cancel': 'ပယ်ဖျက်ရန်',
    'submit': 'တင်သွင်းရန်',
    'close': 'ပိတ်ရန်',
    'back': 'နောက်သို့',
    'next': 'ရှေ့သို့',
    'search': 'ရှာဖွေရန်',
    'filter': 'စစ်ထုတ်ရန်',
    'all': 'အားလုံး',
    'select': 'ရွေးချယ်ရန်',
    'required': 'လိုအပ်သည်',
    'optional': 'ရွေးချယ်နိုင်သည်',
    
    // Games
    'popular_games': 'လူကြိုက်များသောဂိမ်းများ',
    'lottery_games': 'ထီဂိမ်းများ',
    'game_categories': 'ဂိမ်းအမျိုးအစားများ',
    'loading_categories': 'ဂိမ်းအမျိုးအစားများ ဖွင့်နေသည်...',
    'providers': 'ထုတ်လုပ်သူများ',
    'play_now': 'ယခုကစားရန်',
    'launch_game': 'ဂိမ်းကိုဖွင့်ရန်',
    
    // Wallet
    'add_wallet': 'ပိုက်ဆံအိတ်ထည့်ရန်',
    'my_wallets': 'ကျွန်ုပ်၏ပိုက်ဆံအိတ်များ',
    'account_name': 'အကောင့်အမည်',
    'account_number': 'အကောင့်နံပါတ်',
    'payment_method': 'ငွေပေးချေမှုနည်းလမ်း',
    'select_payment_method': 'ငွေပေးချေမှုနည်းလမ်းရွေးချယ်ရန်',
    
    // Cash In/Out
    'amount': 'ပမာဏ',
    'transaction_id': 'ငွေလွှဲအမှတ်',
    'voucher_code': 'ဗူချာကုဒ်',
    'mobile_topup': 'မိုဘိုင်းထည့်ရန်',
    'preset_amounts': 'ကြိုတင်သတ်မှတ်ထားသောပမာဏများ',
    'fee': 'အခကြေးငွေ',
    'total_amount': 'စုစုပေါင်းပမာဏ',
    'back_to_home': 'ပင်မသို့',
    
    // Profile
    'personal_info': 'ကိုယ်ရေးကိုယ်တာအချက်အလက်များ',
    'change_password': 'စကားဝှက်ပြောင်းရန်',
    'current_password': 'လက်ရှိစကားဝှက်',
    'new_password': 'စကားဝှက်အသစ်',
    'confirm_password': 'စကားဝှက်အတည်ပြုရန်',
    'quick_actions': 'လျင်မြန်သောလုပ်ဆောင်ချက်များ',
  },
  en: {
    // Header
    'home': 'Home',
    'games': 'Games',
    'profile': 'Profile',
    'wallet': 'Wallet',
    'cashin': 'Cash In',
    'cashout': 'Cash Out',
    'login': 'Login',
    'register': 'Register',
    'logout': 'Logout',
    'balance': 'Balance',
    'language': 'Language',
    'myanmar': 'မြန်မာ',
    'english': 'English',
    'buffalo_games': 'Buffalo Games',
    'promotions': 'Promotions',
    'invite': 'Invite',
    'rewards': 'Rewards',
    'account': 'Account',
    'slot_games': 'Slot Games',
    'fish_shooting': 'Fish Shooting',
    'live_casino': 'Live Casino',
    'lottery': 'Lottery',
    'sports': 'Sports',
    'hot_games': 'Hot Games',
    'new_games': 'New Games',
    
    // Common
    'loading': 'Loading...',
    'error': 'An error occurred',
    'success': 'Success',
    'save': 'Save',
    'cancel': 'Cancel',
    'submit': 'Submit',
    'close': 'Close',
    'back': 'Back',
    'next': 'Next',
    'search': 'Search',
    'filter': 'Filter',
    'all': 'All',
    'select': 'Select',
    'required': 'Required',
    'optional': 'Optional',
    
    // Games
    'popular_games': 'Popular Games',
    'lottery_games': 'Lottery Games',
    'game_categories': 'Game Categories',
    'loading_categories': 'Loading game categories...',
    'providers': 'Providers',
    'play_now': 'Play Now',
    'launch_game': 'Launch Game',
    'back_to_home': 'Home',
    // Wallet
    'add_wallet': 'Add Wallet',
    'my_wallets': 'My Wallets',
    'account_name': 'Account Name',
    'account_number': 'Account Number',
    'payment_method': 'Payment Method',
    'select_payment_method': 'Select Payment Method',
    
    // Cash In/Out
    'amount': 'Amount',
    'transaction_id': 'Transaction ID',
    'voucher_code': 'Voucher Code',
    'mobile_topup': 'Mobile Top-up',
    'preset_amounts': 'Preset Amounts',
    'fee': 'Fee',
    'total_amount': 'Total Amount',
    
    // Profile
    'personal_info': 'Personal Information',
    'change_password': 'Change Password',
    'current_password': 'Current Password',
    'new_password': 'New Password',
    'confirm_password': 'Confirm Password',
    'quick_actions': 'Quick Actions',
  }
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('my'); // Default to Myanmar

  // Load language from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') as Language;
    if (savedLanguage && (savedLanguage === 'my' || savedLanguage === 'en')) {
      setLanguage(savedLanguage);
    }
  }, []);

  // Save language to localStorage when changed
  const handleSetLanguage = (lang: Language) => {
    try {
      console.log('Setting language to:', lang);
      setLanguage(lang);
      localStorage.setItem('language', lang);
      
      // Update HTML lang attribute
      document.documentElement.lang = lang === 'my' ? 'my' : 'en';
      console.log('HTML lang attribute set to:', document.documentElement.lang);
      
      // Apply Google Translate if needed
      if (typeof window !== 'undefined') {
        // Wait for Google Translate to load
        setTimeout(() => {
          try {
            const googleTranslateElement = document.querySelector('.goog-te-combo') as HTMLSelectElement;
            if (googleTranslateElement) {
              const targetLang = lang === 'my' ? 'my' : 'en';
              console.log('Setting Google Translate to:', targetLang);
              googleTranslateElement.value = targetLang;
              googleTranslateElement.dispatchEvent(new Event('change'));
            } else {
              console.log('Google Translate element not found');
            }
          } catch (error) {
            console.error('Google Translate error:', error);
          }
        }, 100);
      }
    } catch (error) {
      console.error('Language change error:', error);
    }
  };

  const t = (key: string): string => {
    const translation = translations[language][key as keyof typeof translations[typeof language]] || key;
    return translation;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
